import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from './MarketSegmentEdit.module.css';

const MarketSegmentEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    name: ''
  });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [useMockData, setUseMockData] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadMarketSegment();
  }, [id]);

  const loadMarketSegment = async () => {
    try {
      setInitialLoading(true);
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${baseUrl}/api/marketsegments/${id}/edit`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data && data.data.marketSegment) {
          setFormData({
            name: data.data.marketSegment.name || ''
          });
          setUseMockData(false);
        } else {
          throw new Error('Invalid response format');
        }
      } else {
        throw new Error('Failed to load market segment');
      }
    } catch (error) {
      console.error('Error loading market segment:', error);
      setUseMockData(true);
      // Fallback to mock data
      const mockData = getMockMarketSegmentName(id);
      setFormData({
        name: mockData
      });
      toast.warning('Using mock data due to backend connection issue');
    } finally {
      setInitialLoading(false);
    }
  };

  const getMockMarketSegmentName = (segmentId) => {
    const mockSegments = {
      '1': 'Architects',
      '2': 'Central Government',
      '3': 'Charities',
      '4': 'Development',
      '5': 'Education',
      '6': 'Fixed Charge Receiver',
      '7': 'Housing Association',
      '8': 'Insurance',
      '9': 'Investment',
      '10': 'Legal Firms',
      '11': 'Local Authority',
      '12': 'NHS',
      '13': 'Overseas Investors',
      '14': 'Planning Consultants',
      '15': 'Private Estates',
      '16': 'Private Owner',
      '17': 'Property Agent',
      '18': 'Property Manager',
      '19': 'REIT',
      '20': 'Religious',
      '21': 'RETAIL and LEISURE'
    };
    return mockSegments[segmentId] || `Market Segment ${segmentId}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'The name field is required.';
    } else if (formData.name.length > 255) {
      newErrors.name = 'The name may not be greater than 255 characters.';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      if (!useMockData) {
        const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const response = await fetch(`${baseUrl}/api/marketsegments/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(formData)
        });
        
        if (response.ok) {
          const result = await response.json();
                  toast.success('Market segment updated successfully!');
        navigate('/market-segments');
        } else {
          const errorData = await response.json();
          if (errorData.errors) {
            setErrors(errorData.errors);
          } else {
            toast.error(errorData.message || 'Failed to update market segment');
          }
        }
      } else {
        // Mock update
        toast.success('Market segment updated successfully! (Mock)');
        navigate('/market-segments');
      }
    } catch (error) {
      console.error('Error updating market segment:', error);
      toast.error('An error occurred while updating the market segment');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/market-segments');
  };

  if (initialLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingSpinner}>
          <div className={styles.spinner}></div>
          <p>Loading market segment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumbs */}
      <nav className={styles.breadcrumb}>
        <ol>
          <li>
            <a href="/dashboard">Dashboard</a>
          </li>
          <li>
            <a href="/clients">Clients</a>
          </li>
          <li>
            <a href="/market-segments">Market Segments</a>
          </li>
          <li className={styles.active}>Edit Market Segment</li>
        </ol>
      </nav>

      {/* Content Area with Background */}
      <div className={styles.contentArea}>
        {/* Page Title */}
        <div className={styles.pageTitle}>
          <h1>Edit Market Segment</h1>
        </div>

        {/* Form Panel */}
        <div className={styles.formPanel}>
          <div className={styles.panelBody}>
            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.formGroup}>
                <label htmlFor="name" className={styles.formLabel}>
                  Name <span className={styles.required}>*</span>
                </label>
                <div className={styles.inputWrapper}>
                  <input
                    type="text"
                    className={`${styles.formInput} ${errors.name ? styles.inputError : ''}`}
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter market segment name"
                    maxLength={255}
                  />
                  {errors.name && (
                    <div className={styles.errorMessage}>
                      {errors.name}
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.formActions}>
                <button
                  type="submit"
                  className={`${styles.button} ${styles.saveButton}`}
                  disabled={loading}
                >
                  <i className={styles.checkIcon}>✓</i>
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketSegmentEdit; 